/**
 * AssistantContext: global state for the AI assistant chat widget.
 *
 * Manages:
 * - open/close state of the chat drawer
 * - message history (within the current session)
 * - pending NL search filters to be consumed by the Profiles page
 */

import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';

import type { FilterGroup } from '@/types/filters';

// ── Message types ──

export type AssistantMessageRole = 'assistant' | 'user';

export interface AssistantMessage {
  id: string;
  role: AssistantMessageRole;
  content: string;
  timestamp: number;
  /** Metadata attached to assistant response messages */
  meta?: {
    resultCount?: number;
    interpretation?: string;
  };
}

// ── Context value ──

interface AssistantContextValue {
  open: boolean;
  setOpen: (v: boolean) => void;
  toggleOpen: () => void;
  messages: AssistantMessage[];
  addMessage: (msg: Omit<AssistantMessage, 'id' | 'timestamp'>) => void;
  /** Pending filters to be consumed (and cleared) by the Profiles page */
  pendingFilters: FilterGroup | null;
  setPendingFilters: (filters: FilterGroup | null) => void;
  /** Called by the Profiles page after consuming filters */
  consumePendingFilters: () => FilterGroup | null;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

let _msgId = 0;
function nextMsgId() {
  return `assistant-msg-${++_msgId}`;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAssistant(): AssistantContextValue {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistant must be used within AssistantProvider');
  return ctx;
}

const WELCOME_MESSAGE: AssistantMessage = {
  id: 'welcome',
  role: 'assistant',
  content:
    'Hi! I can help you search profiles using natural language. Describe who you\'re looking for (e.g. "CEOs in France with 500+ connections") and I\'ll find them for you.',
  timestamp: Date.now(),
};

export default function AssistantProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<AssistantMessage[]>([WELCOME_MESSAGE]);
  const [pendingFilters, setPendingFilters] = useState<FilterGroup | null>(null);

  const pendingRef = useRef<FilterGroup | null>(null);

  const toggleOpen = useCallback(() => setOpen((v) => !v), []);

  const addMessage = useCallback((msg: Omit<AssistantMessage, 'id' | 'timestamp'>) => {
    setMessages((prev) => [...prev, { ...msg, id: nextMsgId(), timestamp: Date.now() }]);
  }, []);

  const setPendingFiltersWrapped = useCallback((filters: FilterGroup | null) => {
    pendingRef.current = filters;
    setPendingFilters(filters);
  }, []);

  const consumePendingFilters = useCallback(() => {
    const filters = pendingRef.current;
    if (filters) {
      pendingRef.current = null;
      setPendingFilters(null);
    }
    return filters;
  }, []);

  return (
    <AssistantContext.Provider
      value={{
        open,
        setOpen,
        toggleOpen,
        messages,
        addMessage,
        pendingFilters,
        setPendingFilters: setPendingFiltersWrapped,
        consumePendingFilters,
      }}
    >
      {children}
    </AssistantContext.Provider>
  );
}
